var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { forwardRef, useEffect, useMemo, useRef, useState, } from "react";
import "./SelectFilter.css";
import { useMergeRefs } from "../../../common/useFallbackRef";
import { changeOptionToFocus, flatMapOptions, moveToNOptions } from "./Select.function";
import { SelectFilterContext } from "./SelectFilter.context";
import Option from "./components/Option";
import OptGroup from "./components/OptGroup";
var GO_TO_NEXT_FOCUS_OPTION = +1;
var GO_TO_PREVIOUS_FOCUS_OPTION = -1;
var SelectFilter = forwardRef(function (_a, ref) {
    var placeholder = _a.placeholder, id = _a.id, options = _a.options, placeholderFilter = _a.placeholderFilter, filterFunction = _a.filterFunction, value = _a.value, onChange = _a.onChange, onBlur = _a.onBlur, ariaLabelledBy = _a.ariaLabelledBy;
    var _b = useState(""), filter = _b[0], setFilter = _b[1];
    var refSelectBar = useRef(null);
    var refFilter = useRef(null);
    var refOptionsList = useRef(null);
    var refListBox = useRef(null);
    var refSelectVisuel = useRef(null);
    var combinedRefSelect = useMergeRefs([ref, refSelectVisuel]);
    var optionsFlat = useMemo(function () {
        return flatMapOptions(options);
    }, [options]);
    var optionSelected = useMemo(function () {
        if (!value) {
            return undefined;
        }
        var optionSelectedResult = optionsFlat.filter(function (option) { return option.value === value; });
        if (optionSelectedResult.length > 0) {
            return optionSelectedResult.pop();
        }
        return undefined;
    }, [optionsFlat, value]);
    var matchesSearch = function (option, searchText) {
        if (filterFunction) {
            return filterFunction(option, searchText);
        }
        else if (option.name) {
            return option.name.toString().toLowerCase().includes(searchText.toLowerCase());
        }
        return false;
    };
    var _c = useMemo(function () {
        var render = [];
        var flatOptionsFiltered = [];
        options.forEach(function (option) {
            if ("name" in option && "value" in option && matchesSearch(option, filter)) {
                render.push(React.createElement(Option, __assign({}, option)));
                flatOptionsFiltered.push(option);
            }
            else if ("options" in option) {
                var optionsFilteredOfGroup = option.options.filter(function (opt) { return matchesSearch(opt, filter); });
                if (optionsFilteredOfGroup.length > 0) {
                    if (option.disabled) {
                        flatOptionsFiltered = flatOptionsFiltered.concat(optionsFilteredOfGroup.map(function (opt) {
                            opt.disabled = true;
                            return opt;
                        }));
                    }
                    else {
                        flatOptionsFiltered = flatOptionsFiltered.concat(optionsFilteredOfGroup);
                    }
                    render.push(React.createElement(OptGroup, __assign({}, option, { options: optionsFilteredOfGroup })));
                }
            }
        });
        if (flatOptionsFiltered.length === 0) {
            return [
                React.createElement("li", { id: id + "-no-result", className: "ssg-select-filter__option-checkbox-option", tabIndex: 0, onKeyDown: function (e) { return onKeyDownOnNoResult(e); } }, "Aucun r\u00E9sultat"),
                [],
            ];
        }
        return [render, flatOptionsFiltered];
    }, [options, filter]), optionsRendered = _c[0], flatOptionsRendered = _c[1];
    /**
     * ============================
     *  TOGGLE COMBOBOX
     * ============================
     */
    var _d = useState(false), open = _d[0], setOpen = _d[1];
    var toggleOpen = function (event) {
        if (open) {
            close(event);
        }
        else {
            openCombobox(refSelectBar.current);
        }
    };
    var openCombobox = function (focus) {
        setOpen(true);
        if (focus) {
            setElementToFocus(focus);
        }
    };
    var close = function (event) {
        if (open) {
            setOpen(false);
            setFilter("");
            if (refSelectBar.current) {
                setElementToFocus(refSelectBar.current);
            }
            if (onBlur) {
                onBlur(event);
            }
        }
    };
    /**
     * ============================
     *  FOCUS FUNCTION
     * ============================
     */
    var _e = useState(undefined), elementToFocus = _e[0], setElementToFocus = _e[1];
    useEffect(function () {
        if (elementToFocus) {
            var interval_1 = setInterval(function () {
                elementToFocus.focus();
                if (document.activeElement === elementToFocus) {
                    setElementToFocus(undefined);
                    clearInterval(interval_1);
                }
            }, 10);
            return function () {
                clearInterval(interval_1);
            };
        }
        return;
    }, [elementToFocus]);
    /**
     * ============================
     *  KEYDOWN FUNCTION FOR OPTION
     * ============================
     */
    var onKeyDownOnNoResult = function (event) {
        if (event.key === "Tab" && refFilter.current) {
            setElementToFocus(refFilter.current);
        }
    };
    var onChangeValue = function (e, value) {
        if (onChange) {
            onChange(value, e);
        }
        close(e);
    };
    var handleKeyDownOption = function (event, value) {
        if (event.key === "Tab" && refFilter.current) {
            setElementToFocus(refFilter.current);
        }
        else if (event.key === "Enter") {
            onChangeValue(event, value);
        }
        else if (event.key === "ArrowDown") {
            var newElementToFocus = changeOptionToFocus(flatOptionsRendered, value, GO_TO_NEXT_FOCUS_OPTION);
            if (newElementToFocus) {
                setElementToFocus(newElementToFocus);
            }
        }
        else if (event.key === "ArrowUp") {
            var newElementToFocus = changeOptionToFocus(flatOptionsRendered, value, GO_TO_PREVIOUS_FOCUS_OPTION);
            if (newElementToFocus) {
                setElementToFocus(newElementToFocus);
            }
        }
        else if (event.key === "Home") {
            setElementToFocus(moveToNOptions(flatOptionsRendered, 0, GO_TO_NEXT_FOCUS_OPTION));
        }
        else if (event.key === "End") {
            setElementToFocus(moveToNOptions(flatOptionsRendered, flatOptionsRendered.length - 1, GO_TO_PREVIOUS_FOCUS_OPTION));
        }
    };
    /**
     * ============================
     *  KEYDOWN FUNCTION FOR LISTBOX
     * ============================
     */
    var onKeyDownListBox = function (event) {
        if (event.key === "Escape") {
            close(event);
        }
    };
    /**
     * ============================
     *  KEYDOWN FUNCTION FOR FILTER
     * ============================
     */
    var onKeyDownFilter = function (event) {
        if (event.key === "Tab") {
            if (value) {
                var optionRenderedIndex = flatOptionsRendered.findIndex(function (opt) { return opt.value === value; });
                if (optionRenderedIndex !== -1) {
                    openCombobox(moveToNOptions(flatOptionsRendered, optionRenderedIndex, GO_TO_NEXT_FOCUS_OPTION));
                    return;
                }
            }
            openCombobox(moveToNOptions(flatOptionsRendered, 0, GO_TO_NEXT_FOCUS_OPTION));
        }
    };
    /**
     * ============================
     *  KEYDOWN FUNCTION FOR SELECT BAR
     * ============================
     */
    var moveFromSelectBarToOption = function (n) {
        if (flatOptionsRendered.length === 0) {
            openCombobox(refFilter.current);
        }
        var index = 0;
        if (value) {
            index = flatOptionsRendered.findIndex(function (opt) { return opt.value === value; });
            if (index === -1) {
                index = 0;
            }
            else {
                index += n;
            }
        }
        openCombobox(moveToNOptions(flatOptionsRendered, index, n));
    };
    var onKeyDownSelectBar = function (event) {
        if (event.key === "Tab" && onBlur) {
            onBlur(event);
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            openCombobox(refFilter.current);
            return;
        }
        if (event.key === "Escape" || event.key === " ") {
            onChange(null, event);
            return;
        }
        if (event.key === "ArrowDown") {
            moveFromSelectBarToOption(+1);
        }
        if (event.key === "ArrowUp") {
            moveFromSelectBarToOption(-1);
        }
    };
    /**
     * ============================
     *  CLICK OUTSIDE EVENT
     * ============================
     */
    var onClickOutside = function (event) {
        if (refSelectBar &&
            refSelectBar.current &&
            !refSelectBar.current.contains(event.target) &&
            refListBox &&
            refListBox.current &&
            !refListBox.current.contains(event.target)) {
            close(event);
        }
    };
    useEffect(function () {
        document.addEventListener("mousedown", onClickOutside);
        return function unmount() {
            document.removeEventListener("mousedown", onClickOutside);
        };
    }, [onClickOutside]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: combinedRefSelect, id: id, role: "combobox", "aria-haspopup": "listbox", "aria-expanded": open ? "true" : "false", "aria-labelledby": ariaLabelledBy },
            React.createElement(SelectFilterContext.Provider, { value: {
                    onChangeOption: onChangeValue,
                    onKeyDownOption: handleKeyDownOption,
                } },
                React.createElement("div", { ref: refSelectBar, className: "fr-select ssg-select-filter__champs", onClick: toggleOpen, onKeyDown: onKeyDownSelectBar, tabIndex: 0 }, optionSelected ? optionSelected.name : placeholder),
                React.createElement("div", { ref: refListBox, className: "ssg-select-filter__listgroup" },
                    React.createElement("div", { role: "listbox", "aria-labelledby": ariaLabelledBy, className: "ssg-select-filter__listbox", style: { display: open ? "inherit" : "none" }, onKeyDown: onKeyDownListBox },
                        React.createElement("div", { className: "ssg-select-filter__filter" },
                            React.createElement("input", { ref: refFilter, className: "fr-input", name: id + "-filter", role: "textbox", type: "text", "aria-label": "Filtrer les options", placeholder: placeholderFilter !== null && placeholderFilter !== void 0 ? placeholderFilter : "Filtrer...", value: filter, onKeyDown: onKeyDownFilter, onChange: function (e) { return setFilter(e.target.value); } })),
                        React.createElement("ul", { ref: refOptionsList, className: "ssg-select-filter__options-list", tabIndex: -1 }, optionsRendered)))))));
});
export default SelectFilter;
